import { useRef, useState } from 'react';
import axios from 'axios';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, Mail, Phone, Linkedin, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { FadeUp } from './MaskReveal';
import { CONTACT } from '../../lib/site-data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinalCTA() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const glowX = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);
  const glowY = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);

  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle');

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      toast.success('Message received. We’ll be in touch shortly.');
      setForm({ name: '', email: '', company: '', message: '' });
      setTimeout(() => setStatus('idle'), 3000);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not send your message. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong');
      setStatus('idle');
    }
  };

  const inputCls =
    'w-full bg-white/[0.03] border border-white/10 rounded-none px-4 py-4 text-white placeholder-white/35 outline-none transition-colors duration-300 focus:border-signal-500 focus:bg-white/[0.06]';

  return (
    <section
      ref={ref}
      id="contact"
      data-testid="final-cta-section"
      className="relative bg-ink-900 py-20 md:py-28 overflow-hidden"
    >
      {/* Animated blue gradient wash */}
      <motion.div
        style={{ x: glowX, y: glowY }}
        className="pointer-events-none absolute top-1/2 left-1/2 h-[900px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-signal-500/25 blur-[160px] animate-blue-drift"
      />
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="absolute inset-0 noise" />

      <div className="container-editorial relative">
        <div className="max-w-6xl">
          <FadeUp>
            <span className="eyebrow">07 — Let’s build</span>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2
              className="display-heading mt-6 text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl text-balance"
              data-testid="final-cta-headline"
            >
              People will always remain your{' '}
              <span className="italic font-serif text-signal-500" style={{ fontWeight: 400 }}>
                greatest
              </span>{' '}
              advantage.
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-2xl text-white/70 text-lg md:text-xl leading-relaxed">
              Let’s build organizations that attract, develop, engage, and inspire exceptional talent.
            </p>
          </FadeUp>
        </div>

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left – contact details */}
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal-500">
                Contact George &amp; Milton
              </p>
              <h3 className="display-heading mt-4 text-white text-3xl md:text-4xl leading-tight">
                Whether you’re building a leadership team, transforming your culture, or preparing for the next
                phase of growth, we’re here to help.
              </h3>
            </FadeUp>

            <FadeUp delay={0.15}>
              <ul className="mt-12 divide-y divide-white/10 border-t border-b border-white/10">
                <li className="flex items-center justify-between py-5 group">
                  <span className="flex items-center gap-4 text-white/60 text-sm font-mono uppercase tracking-[0.2em]">
                    <Mail className="h-4 w-4 text-signal-500" /> Email
                  </span>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="contact-email"
                    className="text-white text-base hover:text-signal-500 transition-colors duration-300"
                  >
                    {CONTACT.email}
                  </a>
                </li>
                <li className="flex items-center justify-between py-5">
                  <span className="flex items-center gap-4 text-white/60 text-sm font-mono uppercase tracking-[0.2em]">
                    <Phone className="h-4 w-4 text-signal-500" /> Phone
                  </span>
                  <a
                    href={`tel:${CONTACT.phone}`}
                    data-testid="contact-phone"
                    className="text-white text-base hover:text-signal-500 transition-colors duration-300"
                  >
                    {CONTACT.phone}
                  </a>
                </li>
                <li className="flex items-center justify-between py-5">
                  <span className="flex items-center gap-4 text-white/60 text-sm font-mono uppercase tracking-[0.2em]">
                    <Linkedin className="h-4 w-4 text-signal-500" /> LinkedIn
                  </span>
                  <a
                    href={CONTACT.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-linkedin"
                    className="text-white text-base hover:text-signal-500 transition-colors duration-300"
                  >
                    {CONTACT.linkedinLabel}
                  </a>
                </li>
              </ul>
            </FadeUp>
          </div>

          {/* Right – form */}
          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <form
                onSubmit={onSubmit}
                id="contact-form"
                data-testid="contact-form"
                className="relative border border-white/10 bg-black/40 backdrop-blur-md p-6 md:p-10"
              >
                <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                  Secure inquiry
                </span>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Name</span>
                    <input
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      required
                      minLength={2}
                      data-testid="contact-name-input"
                      placeholder="Your full name"
                      className={inputCls + ' mt-2'}
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Email</span>
                    <input
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      type="email"
                      data-testid="contact-email-input"
                      placeholder="you@company.com"
                      className={inputCls + ' mt-2'}
                    />
                  </label>
                </div>

                <label className="block mt-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Company</span>
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    data-testid="contact-company-input"
                    placeholder="Company or organization"
                    className={inputCls + ' mt-2'}
                  />
                </label>

                <label className="block mt-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">What are you building?</span>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    minLength={10}
                    rows={5}
                    data-testid="contact-message-input"
                    placeholder="Tell us about your leadership, culture or people strategy needs…"
                    className={inputCls + ' mt-2 resize-none'}
                  />
                </label>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-white/45 text-xs max-w-xs">
                    We respond to every serious inquiry within two business days.
                  </p>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    data-testid="contact-submit"
                    className={`btn-pill ${status === 'success' ? 'btn-blue' : 'btn-solid'} disabled:opacity-70`}
                  >
                    {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {status === 'success' && <Check className="h-4 w-4" />}
                    {status === 'idle' && <ArrowUpRight className="h-4 w-4" />}
                    {status === 'loading' ? 'Sending…' : status === 'success' ? 'Received' : 'Contact George & Milton'}
                  </button>
                </div>
              </form>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}
